import type { MarkushFragment } from '../domain/markushDocument.ts';
import { drawingDocumentToFragment } from '../domain/markushProjections.ts';
import { importSmiles } from './smilesImport.ts';

// #652 (Markush M3c): R-group members entered as SMILES. The **first atom** of the SMILES is the
// one that bonds to the scaffold (owner decision, docs/markush-structures-model.md): `OC` is
// methoxy attached through O, `c1ccccc1` phenyl. RDKit keeps SMILES atom order, and the import
// numbers graph nodes in that order, so the first atom is always the first graph node.

/** Layout box for a member's own coordinates -- its local space (registration onto the scaffold
 *  is M4's geometry step). */
const FRAGMENT_PAPER = { width: 160, height: 160 };

export async function fragmentFromSmiles(smiles: string): Promise<MarkushFragment> {
  const { document } = await importSmiles(smiles, FRAGMENT_PAPER);
  const attachment = document.structure.graph.nodes[0];
  if (!attachment) throw new Error('Enter a SMILES string for the substituent.');
  return drawingDocumentToFragment(document, attachment.id);
}

/** The palette of common patent substituents (owner-confirmed list), as SMILES with the
 *  attachment atom first. `label` is the chip text, equal to fragmentLabel of the member it adds
 *  (a test keeps the two in step), so palette and member chips read the same way. */
export const COMMON_SUBSTITUENTS: readonly {
  readonly key: string;
  readonly smiles: string;
  readonly label: string;
}[] = [
  { key: 'H', smiles: '[H]', label: '–H' },
  { key: 'F', smiles: 'F', label: '–F' },
  { key: 'Cl', smiles: 'Cl', label: '–Cl' },
  { key: 'Br', smiles: 'Br', label: '–Br' },
  { key: 'I', smiles: 'I', label: '–I' },
  { key: 'OH', smiles: 'O', label: '–OH' },
  { key: 'OCH3', smiles: 'OC', label: '–OCH₃' },
  { key: 'NH2', smiles: 'N', label: '–NH₂' },
  { key: 'CH3', smiles: 'C', label: '–CH₃' },
  { key: 'C2H5', smiles: 'CC', label: '–C₂H₅' },
  { key: 'CF3', smiles: 'C(F)(F)F', label: '–CF₃' },
  { key: 'CN', smiles: 'C#N', label: '–CN' },
  { key: 'NO2', smiles: '[N+](=O)[O-]', label: '–NO₂' },
  { key: 'COOH', smiles: 'C(=O)O', label: '–CO₂H' },
  { key: 'Ph', smiles: 'c1ccccc1', label: '–C₆H₅' }
];
